import { UiElement } from "./UiElement.js";

export { Dropdown };

/**
 * Dropdown menu
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.ui
 */
class Dropdown extends UiElement {

    /**
     * options = {
     *   items: object (object with entries for dropdown),
     *   showDeselectItem: boolean
     *   deselectLabel: string
     * }
     * 
     * @param {HTMLElement | string} target 
     * @param {object} options 
     */
    constructor(target, options) {
        super(target, {
            "select": []
        });

        this.content_ = null;
        this.selected_ = null;
        this.searchbar_ = null;

        this.items_ = [];
        this.allItems_ = [];

        this.placeholder_ = (options && options.placeholder) ? options.placeholder : "select an item ...";
        this.deselectLabel_ = (options && (options.deselectLabel)) ? options.deselectLabel : "... deselect";
        this.showDeselectItem = (options && (typeof options.showDeselectItem == "boolean")) ? options.showDeselectItem : true;

        this.initElement_();

        if (options && options.items) this.setItems(options.items);
        if (options.search) this.initSearch_(options.searchPlaceholder);
    }

    /**
     * initialize the Ui element.
     * Called once in the constructor
     * 
     * @private
     */
    initElement_() {
        const element = this.getElement();
        element.classList.add("vef-dropdown");
        element.innerHTML = `
            <div class='vef-dropdown-header'>
                <div class='vef-dropdown-header-inner'>
                    <span class="preset-title">${this.placeholder_}</span>
                    <i class='fas fa-chevron-down'></i>
                </div>
            </div>
            <div class='vef-dropdown-menu'>
                <div class='vef-dropdown-search'></div>
                <div class='vef-dropdown-content'></div>            
            </div>
        `;

        let open = false;
        const event = () => {
            if (!open) {
                element.classList.add("open");
                if (this.searchbar_) this.searchbar_.focus();
                document.body.addEventListener("click", event);
            } else {
                element.classList.remove("open");
                document.body.removeEventListener("click", event);
            }
            open = !open;
        }

        element.addEventListener("click", (e) => {
            e.stopPropagation();
            if (e.target.nodeName == "INPUT") {
                return;
            } else {
                event();
            }
        });

        this.content_ = element.querySelector(".vef-dropdown-content");
        this.addDeselectItem_();
    }

    /**
     * helper method to add an element to deselect
     * the current value from the dropdown
     * 
     * @private
     */
    addDeselectItem_() {
        if (this.showDeselectItem) {
            const element = this.createItemElement_(this.deselectLabel_, null);

            element.onclick = () => {
                this.deselect();
                this.fire("select", null);
            };

            this.content_.appendChild(element);
        }
    }

    /**
     * Helper method for creating the
     * HTMLElement of an item
     * 
     * @param {string} text 
     * @param {object} css 
     * @returns {HTMLElement} element
     */
    createItemElement_(label, css) {
        const element = document.createElement("div");
        element.classList.add("vef-dropdown-item");

        if (css) {
            for (let i in css) {
                element.style[i] = css[i];
            }
        }

        const text = document.createElement("div");
        text.classList.add("vef-dropdown-item-text");
        text.innerText = label;
        element.appendChild(text);

        return element;
    }

    /**
     * called when an item gets selected.
     * Sets the name in the Ui
     * 
     * @param {object} item
     * @private
     */
    select_(item) {
        if (!item && !this.selected_) return false;

        const header = this.getElement().querySelector(".vef-dropdown-header");
        const title = header.querySelector(".preset-title");

        if (item) {
            title.innerText = item.key;
            this.selected_ = item;
        } else {
            title.innerText = this.placeholder_;
            this.selected_ = null;
        }

        return true;
    }

    /**
     * Set the available items in the dropdown
     * 
     * @param {object} items 
     */
    setItems(items) {
        this.allItems_ = items;
        this.setItems_(items);
    }

    /**
     * Internal method to set the visible items
     * 
     * @param {object} items 
     * @private
     */
    setItems_(items) {
        this.clear();
        for (let i in items) {
            this.addItem(i, items[i]);
        }
    }

    /**
     * Add an item to the dropdown
     * 
     * @param {string} key
     * @param {*} item
     * @param {object} css (optional) styling for dropdown item
     */
    addItem(key, item, css) {
        const element = this.createItemElement_(key, css)
        const itemWrapper = {
            key: key,
            item: item
        };

        element.onclick = () => {
            this.select_(itemWrapper);
            this.fire("select", itemWrapper);
        };

        this.content_.appendChild(element);
        this.items_.push(itemWrapper);
    }

    /**
     * Remove all items
     */
    clear() {
        this.content_.innerHTML = "";
        this.addDeselectItem_();
        this.items_ = [];
    }

    /**
     * Get the currently selected color item
     */
    getSelectedItem() {
        return this.selected_;
    }

    /**
     * Set the dropdown menu to deselected.
     */
    deselect() {
        this.select_(null);
    }

    /**
     * Selet an item by the given key
     * 
     * @param {string} key
     */
    select(key) {
        for (let i = 0; i < this.items_.length; ++i) {
            const item = this.items_[i];
            if (item.key == key) {
                this.select_(item);
                return;
            }
        }
    }

    /**
     * Add search in titles if needed
     * 
     * @private
     */
    initSearch_(placeholder) {
        let element = this.getElement();
        let search = element.querySelector(".vef-dropdown-search")
        search.innerHTML = `<input spellcheck="false" placeholder="${(placeholder) ? placeholder : 'search items ...'}" class="query" type="text"/>`;

        const input = search.querySelector(".query");
        input.addEventListener("focus", (e) => {
            e.stopPropagation();
        });

        // init search events
        let searchTimeout = null;
        input.addEventListener("input", () => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
                this.search(input.value.toLowerCase().trim());
                searchTimeout = null;
            }, 750);
        });

        input.addEventListener("keydown", (e) => {
            if (e.key == "Enter") {
                clearTimeout(searchTimeout);
                this.search(input.value.toLowerCase().trim());
            }
        });

        this.searchbar_ = input;
    }

    /**
     * Search in titles and add to dropdown
     * @param {string} query
     */
    search(query) {
        const filtered = Object.fromEntries(
            Object.entries(this.allItems_).filter(
                ([key, val]) => key.includes(query)
            )
        );

        this.setItems_(filtered);
    }

}